import { InboxItem } from '../aggregates/InboxItem';
import { NextAction } from '../../../project-management/domain/aggregates/NextAction';
import { ActionContext } from '../../../project-management/domain/value-objects/ActionContext';
import { EnergyLevel } from '../../../project-management/domain/value-objects/EnergyLevel';

/**
 * GTD Processing Workflow Service
 * 
 * Implements David Allen's complete GTD methodology by orchestrating the flow
 * from inbox clarification to actionable work items (NextActions, Projects, etc.)
 * 
 * Core GTD Decision Tree:
 * 1. What is it? (Clarification)
 * 2. Is it actionable? (Decision)
 * 3. If YES: What's the next action? (NextAction creation)
 * 4. If NO: Reference material, someday/maybe, or trash
 */
export class GTDProcessingWorkflowService {
    
    /**
     * Process a clarified inbox item according to GTD methodology
     * 
     * @param inboxItem - The clarified inbox item
     * @param processingDecision - How the item should be processed
     * @returns The created work items (NextActions, Projects, etc.)
     */
    processInboxItem(
        inboxItem: InboxItem, 
        processingDecision: InboxProcessingDecision
    ): GTDProcessingResult {
        
        if (!inboxItem.clarification) {
            throw new Error('Inbox item must be clarified before processing');
        }

        if (inboxItem.processingStatus === 'unprocessed') {
            throw new Error('Inbox item must be marked as processed');
        }

        // Execute GTD decision tree
        if (inboxItem.isActionable === true) {
            return this.handleActionableItem(inboxItem, processingDecision);
        } else {
            return this.handleNonActionableItem(inboxItem, processingDecision);
        }
    }

    /**
     * Handle actionable items (create NextActions or Projects)
     */
    private handleActionableItem(
        inboxItem: InboxItem, 
        decision: InboxProcessingDecision
    ): GTDProcessingResult {
        
        const result: GTDProcessingResult = {
            success: true,
            workflowType: 'actionable',
            createdItems: []
        };

        // GTD Rule: If it takes less than 2 minutes, do it now
        // Otherwise, defer it (NextAction) or delegate it
        
        if (decision.estimatedMinutes && decision.estimatedMinutes <= 2) {
            // Create immediate action
            const nextAction = this.createNextAction(inboxItem, decision);
            result.createdItems.push({
                type: 'next_action',
                id: nextAction.id,
                description: nextAction.description,
                urgent: true,
                reason: 'Two-minute rule: Do it now'
            });
        } else if (decision.isProject) {
            // Multi-step outcomes require a project
            result.workflowType = 'project';
            result.createdItems.push({
                type: 'project',
                id: `project-${Date.now()}`,
                description: decision.projectOutcome || inboxItem.clarification || 'New project',
                reason: 'Multi-step outcome requires project planning'
            });
            
            // Create the first next action for the project
            const firstAction = this.createNextAction(inboxItem, decision);
            result.createdItems.push({
                type: 'next_action',
                id: firstAction.id,
                description: firstAction.description,
                urgent: false,
                reason: 'First action for project'
            });
        } else {
            // Single next action
            const nextAction = this.createNextAction(inboxItem, decision);
            result.createdItems.push({
                type: 'next_action',
                id: nextAction.id,
                description: nextAction.description,
                urgent: false,
                reason: 'Single actionable item'
            });
        }

        return result;
    }

    /**
     * Handle non-actionable items (reference, someday/maybe, trash)
     */
    private handleNonActionableItem(
        inboxItem: InboxItem, 
        decision: InboxProcessingDecision
    ): GTDProcessingResult {
        
        const result: GTDProcessingResult = {
            success: true,
            workflowType: 'non_actionable',
            createdItems: []
        };

        if (decision.isReference) {
            // Store as reference material
            result.createdItems.push({
                type: 'reference_material',
                id: `ref-${Date.now()}`,
                description: inboxItem.clarification || 'Reference material',
                reason: 'Useful information for future reference'
            });
        } else if (decision.isSomedayMaybe) {
            // Add to Someday/Maybe list
            result.createdItems.push({
                type: 'someday_maybe',
                id: `someday-${Date.now()}`,
                description: inboxItem.clarification || 'Someday/Maybe item',
                reason: 'Potentially actionable in the future'
            });
        } else {
            // Trash - no action needed
            result.workflowType = 'trash';
            result.reason = 'Not actionable and not worth keeping';
        }

        return result;
    }

    /**
     * Create a NextAction from an inbox item
     */
    private createNextAction(
        inboxItem: InboxItem, 
        decision: InboxProcessingDecision
    ): NextAction {
        
        // Determine context - default to @computer if not specified
        const context = decision.context 
            ? this.parseActionContext(decision.context)
            : ActionContext.atComputer();

        // Determine energy level - default to medium
        const energyLevel = decision.energyLevel
            ? this.parseEnergyLevel(decision.energyLevel)
            : EnergyLevel.medium();

        // Estimate time - default to 1 minute
        const estimatedMinutes = decision.estimatedMinutes || 1;

        // Create the action description
        const actionDescription = decision.nextActionDescription 
            || this.generateActionDescription(inboxItem.clarification || '');

        return NextAction.create(
            actionDescription,
            context,
            energyLevel,
            estimatedMinutes,
            inboxItem.capturedByPersonId,
            decision.projectId
        );
    }

    /**
     * Parse context string into ActionContext object
     */
    private parseActionContext(contextString: string): ActionContext {
        const context = contextString.toLowerCase().replace('@', '');
        
        switch (context) {
            case 'computer':
                return ActionContext.atComputer();
            case 'phone':
            case 'calls':
                return ActionContext.onPhone();
            case 'office':
                return ActionContext.atOffice();
            case 'home':
                return ActionContext.atHome();
            case 'errands':
                return ActionContext.errands('general');
            default:
                return ActionContext.custom(context);
        }
    }

    /**
     * Parse energy level string into EnergyLevel object
     */
    private parseEnergyLevel(energyString: string): EnergyLevel {
        const level = energyString.toLowerCase();
        
        switch (level) {
            case 'high':
                return EnergyLevel.high();
            case 'low':
                return EnergyLevel.low();
            case 'medium':
            default:
                return EnergyLevel.medium();
        }
    }

    /**
     * Generate a proper action description from clarification
     * GTD Principle: Actions must be specific and physical
     */
    private generateActionDescription(clarification: string): string {
        // Simple heuristic to make actions more specific
        const actionVerbs = ['Call', 'Email', 'Write', 'Research', 'Review', 'Schedule', 'Update'];
        
        // If clarification already starts with an action verb, use it
        const hasActionVerb = actionVerbs.some(verb => 
            clarification.toLowerCase().startsWith(verb.toLowerCase())
        );

        if (hasActionVerb) {
            return clarification;
        }

        // Otherwise, prefix with a default action verb
        return `Research: ${clarification}`;
    }
}

/**
 * Input for processing decisions
 */
export interface InboxProcessingDecision {
    // Action characteristics
    estimatedMinutes?: number;
    context?: string; // @calls, @computer, @errands, etc.
    energyLevel?: string; // high, medium, low
    nextActionDescription?: string;
    
    // Workflow decisions
    isProject?: boolean;
    projectOutcome?: string;
    projectId?: string;
    
    // Non-actionable decisions
    isReference?: boolean;
    isSomedayMaybe?: boolean;
}

/**
 * Result of GTD processing workflow
 */
export interface GTDProcessingResult {
    success: boolean;
    workflowType: 'actionable' | 'project' | 'non_actionable' | 'trash';
    createdItems: Array<{
        type: 'next_action' | 'project' | 'reference_material' | 'someday_maybe';
        id: string;
        description: string;
        urgent?: boolean;
        reason: string;
    }>;
    reason?: string;
    error?: string;
}